import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearOrders,
  setTourParameter,
  setTerritoryParameter,
  setUserParam,
  saveUserSettings,
  setSolutionJson,
} from 'actions';
import withTranslation from 'hoc/withTranslation';
import config from 'config';
import { find, isEqual, map, isEmpty } from 'lodash';
import styled from '@emotion/styled';
import TerritoryPlanningContainer from 'components/FloatingPanels/TerritoryPlanning/TerritoryPlanningContainer';
import { PANELS } from 'reducers/user';
import moment from 'moment/moment';
import Checkbox from '../../Form/Checkbox';
import TimeRangeSlider from './TimeRangeSlider';
import { isMobileDevice, isProdEnv } from '../../../utils/helpers';
import {
  StyledDoublePanel,
  StyledLine,
  StyledTextInput,
  StyledWizardContent,
  StyledWizardH1,
  StyledSelectBigInput,
  StyledIcon,
  StyledSelectedInputLabel,
  StyledTriplePanel,
  StyledInformationNormalIcon,
  StyledSpace,
} from '../Global/WizardStyled';
import InputLocation from '../Global/InputLocation';
import Section from '../../Form/Section';
import Label from '../../Form/Label';
import Selector from '../../Form/Selector';
import iconCapacity from '../../../global/img/shipment.svg';
import { AmplitudeService, AMPLITUDE_EVENTS } from '../../../utils/amplitude';
import { getFleetTypeIcon } from '../../../utils/FleetHelpers';
import NumericInput from '../Global/NumericInput';
import FleetEndAddress from './FleetEndAddress';
import { APP_MODES, getAppMode } from '../../../utils/urlHelpers';
import { getSafeValue } from '../../../utils/security';
import { createRandomTerritories } from '../../../utils/territories/TerritoriesHelpers';
import { getAtLocation } from '../../../utils/GeoCoder';
import { getBoundingCoordinates } from '../../../utils/MapHelpers';
import { colors, fontSize } from '../../../global/variables';
import EVSelector from './EVSelector';

const appMode = getAppMode();
const isProd = isProdEnv();
const isEVFF = appMode.includes(APP_MODES.EV_PROFILES);

const { secondaryTextColor, aqua } = colors;
const { small } = fontSize;

const isMobile = isMobileDevice();
const {
  scenario: { times: timesConfig },
  maxValues,
  shortTimeFormat,
} = config;

const StyledFleetSelector = styled.div(({ align }) => ({
  height: '2rem',
  justifySelf: align,
  width: '100%',
}));

const StyledEVCheckboxWrapper = styled.div({
  alignSelf: 'center',
  marginTop: '0.8rem',
});

const StyledTerritoryPanel = styled.div`
  display: grid;
  grid-template-columns: fit-content(50%) fit-content(10%) fit-content(40%);
  justify-content: start;
  grid-gap: 2rem;
`;

const StyledDropText = styled.span({
  fontWeight: '700',
  height: '1.75rem',
  fontSize: small,
  color: secondaryTextColor,
  marginRight: '0.5rem',
  span: {
    color: aqua,
    ':hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
});

let hasRestoredFromLocalStorage = false;

const Step1Container = ({
  user,
  oAuth,
  tourPlanner,
  mapData,
  onTerritoryPlanningPanelToggle,
  solution,
  translations: {
    error: { depotNotFound },
    global: {
      timesTrans,
      costTrans: { timeLabel, distanceKmLabel, distanceMileLabel, fixedLabel },
    },
    locationPopup: { fleetTrans, infoEnableSettings, fleetEnabledTrans, planEnabledTrans },

    map: { depotMarkerTrans },
    tourPlanner: {
      depotLabelTrans,
      depotPlaceholderTrans,
      configurationLabelTrans,
      costLabelTrans,
      timeLabelTrans,
    },
    territoryPlanning: {
      territoryLabelTrans,
      territorySwitchLabel,
      territoryPlanningInformation,
      territoryStrictSwitchLabel,
    },
    wizard: {
      fleet: {
        vehiclesAmountLabel,
        vehiclesCapacityLabel,
        vehiclesAmountLabelMobile,
        vehiclesCapacityLabelMobile,
        fleetTypeTitle,
        fleetTypes,
      },
    },
  },
}) => {
  const currentStep = useSelector((state) => state.usageContext.currentStep);
  const territories = useSelector(({ areas: stateAreas }) => stateAreas.territories);
  const dispatch = useDispatch();
  const handleClearOrders = useCallback((data) => dispatch(clearOrders(data)), [dispatch]);
  const handleSetTourParameter = useCallback(
    (parameter) =>
      dispatch(setTourParameter({ ...parameter, index: solution.show, userChange: true })),
    [dispatch, solution.show],
  );
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);

  const handleSetTerritoryParameter = useCallback(
    (parameter) => dispatch(setTerritoryParameter(parameter)),
    [dispatch],
  );

  const {
    time: { key: timeTourParamKey, value: timeTourParamValue },
    vehicles: [{ amount, capacity, costs }],
    location,
    vehicleProfiles: [vehicleProfile],
  } = tourPlanner || {};
  const { fleetType, isElectricVehicle: savedIsEV } = vehicleProfile || {};
  const { time, distance, fixed } = costs;

  const [times, setTimes] = useState(timeTourParamValue);
  const [fleetAmount, setFleetAmount] = useState(amount);
  const [fleetCapacity, setFleetCapacity] = useState(capacity);
  const [noDepotFound, setNoDepotFound] = useState(false);
  const [showTerritoryPlanning, setShowTerritoryPlanning] = useState(false);
  const [isElectricVehicle, setIsElectricVehicle] = useState(
    isEVFF && (savedIsEV !== undefined ? savedIsEV : fleetType === 'electricVehicle'),
  );
  const [tpObj] = useState(() => {
    const savedData = localStorage.getItem('userSettings');
    return savedData ? JSON.parse(savedData) : tourPlanner;
  });
  const fleetEnabled = user.fleetEnabled;
  const planEnabled = user.planEnabled;
  const lastImport = user.lastImport;
  const csvOrders = lastImport?.filename?.endsWith('.csv') && !isEmpty(lastImport.delimiter);

  const tpVersion = user?.tpaVersion?.version;
  const isDevOrSit = tpVersion === 'dev' || tpVersion === 'sit';
  const canFleetTypeBeElectric = (type) =>
    isEVFF && isDevOrSit && (type === 'taxi' || type === 'car');

  const cleanJSONFile = useCallback(() => {
    if (tourPlanner.fileType === 'json' && !csvOrders) handleClearOrders({ index: solution.show });
  }, [tourPlanner, solution.show, csvOrders]);

  useEffect(() => cleanJSONFile(), []);

  useEffect(() => {
    setFleetCapacity(capacity);
    setFleetAmount(amount);
  }, [amount, capacity]);

  useEffect(() => {
    onTerritoryPlanningPanelToggle(showTerritoryPlanning);
  }, [showTerritoryPlanning, onTerritoryPlanningPanelToggle]);

  const timeOptions = useMemo(
    () => map(timesConfig, ({ key }) => ({ key, value: getSafeValue(timesTrans, key) })),
    [timesTrans],
  );

  const costLabels = [
    timeLabel,
    user.distance === 'imperial' ? distanceMileLabel : distanceKmLabel,
    fixedLabel,
  ];

  const onHandleSetCost = useCallback(
    (value, id) => {
      if (id === 'time-cost')
        handleSetTourParameter({
          vehicles: [
            { ...tourPlanner.vehicles[0], costs: { time: parseFloat(value), distance, fixed } },
          ],
        });
      if (id === 'distance-cost')
        handleSetTourParameter({
          vehicles: [
            { ...tourPlanner.vehicles[0], costs: { time, distance: parseFloat(value), fixed } },
          ],
        });
      if (id === 'fixed-cost')
        handleSetTourParameter({
          vehicles: [
            { ...tourPlanner.vehicles[0], costs: { time, distance, fixed: parseFloat(value) } },
          ],
        });

      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_VEHICLE_COSTS);
    },
    [handleSetTourParameter, capacity, amount, costs, tourPlanner],
  );

  const onSliderChange = useCallback(
    ({ start, end }) => {
      const newTimes = { start, end };

      if (!isEqual(newTimes, times)) {
        const { key } = find(timesConfig, ({ value }) => isEqual(newTimes, value)) || { key: null };
        setTimes(newTimes);
        handleSetTourParameter({
          time: { key, value: newTimes },
          offset: moment.parseZone(start, shortTimeFormat).utcOffset(),
        });
        AmplitudeService.log(AMPLITUDE_EVENTS.TIME_CONFIG_CHANGED);
      }
    },
    [handleSetTourParameter, setTimes, times],
  );

  const onTimeChange = useCallback(
    (timeKey) => {
      const { value } = find(timesConfig, ({ key }) => timeKey === key) || {};
      if (value) {
        setTimes(value);
        handleSetTourParameter({ time: { key: timeKey, value } });
      }
      AmplitudeService.log(AMPLITUDE_EVENTS.TIME_CONFIG_CHANGED);
    },
    [handleSetTourParameter, setTimes],
  );

  const onAmountChange = useCallback(
    (e) => {
      const value = e.target.value && parseInt(e.target.value, 10);
      setFleetAmount(value);
      e.target.focus();
    },
    [setFleetAmount],
  );

  const onAmountBlur = useCallback(() => {
    const value = parseInt(fleetAmount, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= maxValues.vehicles) {
      handleSetTourParameter({ vehicles: [{ ...tourPlanner.vehicles[0], amount: value }] });
    } else {
      setFleetAmount(amount);
    }
    AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_VEHICLE_NUMBER);
  }, [handleSetTourParameter, setFleetAmount, amount, capacity, fleetAmount, tourPlanner]);

  const onCapacityChange = useCallback(
    (e) => {
      const value = e.target.value && parseInt(e.target.value, 10);
      setFleetCapacity([value]);
      e.target.focus();
    },
    [setFleetCapacity],
  );

  const onCapacityBlur = useCallback(() => {
    const value = parseInt(fleetCapacity[0], 10);
    if (!Number.isNaN(value) && value >= 1 && value <= maxValues.capacity) {
      handleSetTourParameter({ vehicles: [{ ...tourPlanner.vehicles[0], capacity: [value] }] });
    } else {
      setFleetCapacity([capacity]);
    }
    AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_CAPACITY);
  }, [handleSetTourParameter, setFleetCapacity, capacity, amount, fleetCapacity, tourPlanner]);

  const onLocationFound = useCallback(
    (loc) => {
      setNoDepotFound(!loc.value && !isEmpty(loc.label));
      dispatch(setSolutionJson({ depotChange: true }));
      handleSetTourParameter({ location: { ...loc }, uploadedLocations: false });
      if (loc && loc.value) AmplitudeService.log(AMPLITUDE_EVENTS.DEPOT_SET);
    },
    [handleSetTourParameter, setNoDepotFound],
  );

  const handleVehicleTypeChange = useCallback(
    (e) => {
      const newFleetType = e.target.value;

      if (isElectricVehicle && !canFleetTypeBeElectric(newFleetType)) {
        setIsElectricVehicle(false);
      }

      handleSetTourParameter({
        vehicleProfiles: [
          {
            ...tourPlanner.vehicleProfiles[0],
            fleetType: newFleetType,
            isElectricVehicle: isElectricVehicle && canFleetTypeBeElectric,
          },
        ],
      });

      AmplitudeService.log(AMPLITUDE_EVENTS.FLEET_CONFIG_CHANGE_FLEET_TYPE, {
        fleetType: newFleetType,
      });
    },
    [handleSetTourParameter, tourPlanner, isElectricVehicle],
  );

  const handleElectricVehicleToggle = useCallback(
    (checked) => {
      setIsElectricVehicle(checked);

      const updatedProfile = {
        ...tourPlanner.vehicleProfiles[0],
        fleetType,
        isElectricVehicle: checked,
      };

      if (!checked) {
        delete updatedProfile.evProfile;

        handleSetTourParameter({
          vehicleProfiles: [updatedProfile],
        });
      } else {
        handleSetTourParameter({
          vehicleProfiles: [updatedProfile],
        });
      }
    },
    [handleSetTourParameter, tourPlanner, fleetType],
  );

  const handleEVProfileChange = useCallback(
    (evProfileData) => {
      if (evProfileData && isElectricVehicle) {
        handleSetTourParameter({
          vehicleProfiles: [
            {
              ...tourPlanner.vehicleProfiles[0],
              fleetType,
              isElectricVehicle: true,
              evProfile: {
                make: evProfileData.make,
                model: evProfileData.model,
                variant: evProfileData.variant,
                profileId: evProfileData.profileId,
                maxDistance: evProfileData.maxDistance,
                specs: evProfileData.specs,
              },
            },
          ],
        });
      }
    },
    [handleSetTourParameter, tourPlanner, fleetType, isElectricVehicle],
  );

  const handleOnTerritoriesEnabled = useCallback(
    (val) => {
      const loc = getAtLocation(user, tourPlanner);
      const bbox =
        mapData && mapData.geo && mapData.geo.features.length >= 2
          ? mapData.geo.bbox
          : getBoundingCoordinates(loc, 300);

      let territoryAreas;
      if (appMode.includes(APP_MODES.TERRA)) {
        setShowTerritoryPlanning(val);
      } else if (val) {
        territoryAreas = createRandomTerritories(bbox);
      }
      handleSetTerritoryParameter({ isEnabled: val, areaDetails: territoryAreas, bbox });
      handleSetTerritoryParameter({ strict: true });
    },
    [tourPlanner, user, mapData],
  );

  const onOpenClick = () => {
    handleSetUserParam({ openPanel: PANELS.MAP_SETTINGS });
  };

  const handleOnCloseTerritoryPlanning = useCallback(
    (territoryPlanningState) => {
      setShowTerritoryPlanning(false);
      handleSetTerritoryParameter({ isEnabled: territoryPlanningState });
    },
    [setShowTerritoryPlanning],
  );

  const handleStrictChange = useCallback(
    (val) => {
      handleSetTerritoryParameter({ strict: val });
    },
    [handleSetTerritoryParameter],
  );

  useEffect(() => {
    dispatch(saveUserSettings(tourPlanner));
  }, [tourPlanner, dispatch]);

  useEffect(() => {
    if (!hasRestoredFromLocalStorage && tpObj && Object.keys(tpObj).length > 0) {
      handleSetTourParameter({
        ...tpObj,
        ordersMode: tourPlanner.ordersMode,
        solverConfiguration: tourPlanner.solverConfiguration,
      });
      hasRestoredFromLocalStorage = true;
    }
  }, [tourPlanner.ordersMode, tourPlanner.solverConfiguration]);

  useEffect(() => {
    hasRestoredFromLocalStorage = false;
  }, [currentStep]);

  const show = false;
  // only temporary due to adding limits for vehicles
  const showDriverSchedule = false;

  return (
    <StyledWizardContent>
      {showTerritoryPlanning ? (
        <TerritoryPlanningContainer
          onClose={handleOnCloseTerritoryPlanning}
          user={user}
          tourPlanner={tourPlanner}
          oAuth={oAuth}
        />
      ) : (
        <>
          <StyledWizardH1>{depotMarkerTrans}</StyledWizardH1>
          <Section>
            {!noDepotFound && <Label>{depotLabelTrans}</Label>}
            {noDepotFound && <Label error>{depotNotFound}</Label>}
            <InputLocation
              id="input-start"
              placeholder={depotPlaceholderTrans}
              value={location.label}
              oAuth={oAuth}
              onLocationFound={onLocationFound}
              user={user}
              tourPlanner={tourPlanner}
              hasCurrentLocationControl
            />
            <FleetEndAddress
              oAuth={oAuth}
              user={user}
              tourPlanner={tourPlanner}
              solution={solution}
            />
          </Section>
          <StyledLine />
          {show && appMode.includes(APP_MODES.WIP, APP_MODES.TERRA) && (
            <>
              <StyledWizardH1>{territoryLabelTrans}</StyledWizardH1>
              <Section>
                <StyledTerritoryPanel>
                  <Checkbox
                    id="service-area-enabler"
                    label={territorySwitchLabel}
                    value={territories.isEnabled}
                    onChange={handleOnTerritoriesEnabled}
                  />
                  <StyledIcon
                    id="information-btn"
                    icon={StyledInformationNormalIcon}
                    title={territoryPlanningInformation}
                    mini
                  />
                  {territories.isEnabled && (
                    <Checkbox
                      id="service-area-strict"
                      label={territoryStrictSwitchLabel}
                      value={territories.strict}
                      onChange={handleStrictChange}
                    />
                  )}
                </StyledTerritoryPanel>
              </Section>
              <StyledLine />
            </>
          )}
          <StyledWizardH1>{configurationLabelTrans}</StyledWizardH1>
          <StyledDoublePanel hasControls>
            <StyledFleetSelector align="start">
              <StyledSelectedInputLabel asBlock>{fleetTypeTitle}</StyledSelectedInputLabel>
              <StyledSelectBigInput
                fullWidth
                onChange={handleVehicleTypeChange}
                value={fleetType}
                icon={getFleetTypeIcon(fleetType)}
                id="input-fleet-type"
              >
                {Object.keys(fleetTypes)
                  .filter((typeID) => typeID !== 'electricVehicle')
                  .map((typeID) => (
                    <option key={typeID} value={typeID}>
                      {getSafeValue(fleetTypes, typeID)}
                    </option>
                  ))}
              </StyledSelectBigInput>
            </StyledFleetSelector>
            {canFleetTypeBeElectric(fleetType) && (
              <StyledEVCheckboxWrapper>
                <Checkbox
                  id="electric-vehicle-checkbox"
                  label="Electric Vehicle"
                  value={isElectricVehicle}
                  onChange={handleElectricVehicleToggle}
                />
              </StyledEVCheckboxWrapper>
            )}
          </StyledDoublePanel>
          {isEVFF && isDevOrSit && isElectricVehicle && (
            <EVSelector
              oAuth={oAuth}
              apiKey={user.apiKey}
              onEVProfileChange={handleEVProfileChange}
              initialProfile={vehicleProfile?.evProfile}
            />
          )}
          <>
            <Section>
              <StyledDoublePanel>
                <div>
                  <Label>{isMobile ? vehiclesAmountLabelMobile : vehiclesAmountLabel}</Label>
                  <StyledTextInput
                    id="input-fleet-amount"
                    icon={getFleetTypeIcon(fleetType)}
                    type="number"
                    pattern="[0-9]*"
                    inputmode="numeric"
                    min="1"
                    max={maxValues.vehicles}
                    onChange={onAmountChange}
                    value={fleetAmount}
                    onBlur={onAmountBlur}
                  />
                </div>
                <div>
                  <Label>{isMobile ? vehiclesCapacityLabelMobile : vehiclesCapacityLabel}</Label>
                  <StyledTextInput
                    id="input-fleet-capacity"
                    icon={iconCapacity}
                    type="number"
                    pattern="[0-9]*"
                    inputmode="numeric"
                    min="1"
                    max={maxValues.capacity}
                    onChange={onCapacityChange}
                    value={fleetCapacity[0]}
                    onBlur={onCapacityBlur}
                  />
                </div>
              </StyledDoublePanel>
              <StyledSpace />
              {!isProd && (
                <StyledDropText>
                  <div>{fleetTrans}</div>
                  <span onClick={onOpenClick}>{`${infoEnableSettings} ${
                    fleetEnabled ? `- ${fleetEnabledTrans}` : ''
                  } ${planEnabled ? `- ${planEnabledTrans}` : ''}`}</span>
                </StyledDropText>
              )}
            </Section>
            <Section>
              <Section>
                <Label>{timeLabelTrans}</Label>
                {showDriverSchedule && (
                  <Selector
                    id="button-time-window"
                    initialValue={timeTourParamKey}
                    options={timeOptions}
                    onChange={onTimeChange}
                  />
                )}
              </Section>
              <TimeRangeSlider
                id="slider-time-window"
                onChange={onSliderChange}
                timeAggregation={3}
                times={times}
              />
            </Section>
            <Section>
              <Label>{costLabelTrans}</Label>
              <StyledTriplePanel>
                {Object.entries(costs).map((data, index) => {
                  const costValue = Object.values(data);
                  const inputId = `${costValue[0]}-${'cost'}`;
                  return (
                    <NumericInput
                      inputLabel={getSafeValue(costLabels, index)}
                      initialValue={costValue[1]}
                      id={inputId}
                      onChange={onHandleSetCost}
                      key={costValue[0]}
                      maxValue={maxValues.costs}
                      minValue={0}
                    />
                  );
                })}
              </StyledTriplePanel>
            </Section>
          </>
        </>
      )}
    </StyledWizardContent>
  );
};

export default withTranslation(Step1Container);
